import axios from "axios";
import { makeAxiosRequest } from "src/api/utils/apiClient.ts";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import {
	PartConfigurationCurrentInput,
	PartConfigurationSchema,
	PartConfigurationTarget,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";

/**
 * The target of a configurator, or undefined when a target cannot express it: a pinned part revision or a
 * sub-configurator, whose configurations belong to their parent's tree.
 * The backend's `PartConfiguratorType.toPartConfigurationTarget` maps the same way; change both together.
 */
export function getPartConfigurationTarget(
	configuratorType: PartConfiguratorType,
): PartConfigurationTarget | undefined {
	switch (configuratorType.type) {
		case "customerOrderOfferLine":
			return {
				type: "customerOfferLine",
				customerOfferLineId: configuratorType.customerOrderOfferLineId,
				partConfigurationId: configuratorType.explicitPartConfigurationId ?? undefined,
				testContextEnabled: configuratorType.testContextEnabled,
			};
		case "customerOrderLine":
			return {
				type: "customerOrderLine",
				customerOrderLineId: configuratorType.customerOrderLineId,
				partConfigurationId: configuratorType.explicitPartConfigurationId ?? undefined,
				testContextEnabled: configuratorType.testContextEnabled,
			};
		case "shopOrder":
			return {
				type: "shopOrder",
				shopOrderId: configuratorType.shopOrderId,
				partConfigurationId: configuratorType.explicitPartConfigurationId ?? undefined,
			};
		case "shopOrderLine":
			return {
				type: "shopOrderLine",
				shopOrderLineId: configuratorType.shopOrderLineId,
				partConfigurationId: configuratorType.explicitPartConfigurationId ?? undefined,
			};
		case "purchaseOrderLine":
			return {
				type: "purchaseOrderLine",
				purchaseOrderLineId: configuratorType.purchaseOrderLineId,
				partConfigurationId: configuratorType.explicitPartConfigurationId ?? undefined,
			};
		case "projectActivityMaterial":
			return {
				type: "projectActivityMaterial",
				projectActivityMaterialId: configuratorType.activityMaterialLineId,
				partConfigurationId: configuratorType.explicitPartConfigurationId ?? undefined,
			};
		case "salesPart":
			if (configuratorType.partRevisionId != null) return undefined;
			return { type: "salesPart", salesPartId: configuratorType.salesPartId };
		case "sitePart":
			return { type: "part", partId: configuratorType.partId };
		case "configurationTemplateEditor":
			return {
				type: "partConfigurationTemplate",
				partConfigurationTemplateId: configuratorType.partConfigurationTemplateId,
			};
		case "unsavedCustomerOfferLine":
			return {
				type: "unsavedCustomerOfferLine",
				customerOfferId: configuratorType.customerOfferId,
				salesPartId: configuratorType.salesPartId,
				plannedDeliveryDate: configuratorType.plannedDeliveryDate,
				initialValuesFromConfigurationId: configuratorType.initialValuesFromConfigurationId ?? undefined,
			};
		case "unsavedCustomerOrderLine":
			return {
				type: "unsavedCustomerOrderLine",
				customerOrderId: configuratorType.customerOrderId,
				salesPartId: configuratorType.salesPartId,
				plannedDeliveryDate: configuratorType.plannedDeliveryDate,
				initialValuesFromConfigurationId: configuratorType.initialValuesFromConfigurationId ?? undefined,
			};
		case "unsavedShopOrder":
			return { type: "unsavedShopOrder", partId: configuratorType.partId };
		case "unsavedProjectActivityMaterial":
			return {
				type: "unsavedProjectActivityMaterial",
				projectActivityId: configuratorType.projectActivityId,
				partId: configuratorType.partId,
				initialValuesFromConfigurationId: configuratorType.initialValuesFromConfigurationId ?? undefined,
			};
		case "partConfigurationBomLine":
			return {
				type: "partConfigurationBomLine",
				partConfigurationBomLineId: configuratorType.partConfigurationBomLineId,
			};
		case "catalogPartTest":
			return {
				type: "catalogPartTest",
				catalogPartRevisionId: configuratorType.catalogPartRevisionId,
				productFamilyVersionId: configuratorType.productFamilyVersionId,
			};
		case "sitePartTest":
			return { type: "sitePartTest", sitePartRevisionId: configuratorType.sitePartRevisionId };
		case "subConfigurator":
			return undefined;
	}
}

const PART_CONFIGURATIONS_URL = "/api/v1/erp/configurator/partConfigurations";

export async function getPartConfigurationSchema(target: PartConfigurationTarget): Promise<PartConfigurationSchema> {
	return await requestPartConfigurations<PartConfigurationSchema>("getSchema", target);
}

export async function getPartConfigurationCurrentInput(
	target: PartConfigurationTarget,
): Promise<PartConfigurationCurrentInput> {
	return await requestPartConfigurations<PartConfigurationCurrentInput>("getCurrentInput", target);
}

async function requestPartConfigurations<T>(endpoint: string, target: PartConfigurationTarget): Promise<T> {
	try {
		return await makeAxiosRequest<T>({
			url: `${PART_CONFIGURATIONS_URL}/${endpoint}`,
			method: "POST",
			data: { request: { target } },
		});
	} catch (error) {
		throw asUserInterruption(error);
	}
}

/**
 * These endpoints answer a rejected request, such as a target the user cannot see, with a 4xx error body that does not
 * say how to log it. Such a rejection is shown to the user as an interruption, not reported as an unexpected error.
 */
export function asUserInterruption(error: unknown): unknown {
	const status = axios.isAxiosError(error) ? error.response?.status : undefined;
	const data: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
	if (status == null || status < 400 || status >= 500 || typeof data !== "object" || data == null) return error;
	return { ...data, logAsError: false };
}
